import React from 'react';
import clsx from 'clsx';

import Icon from './Icon';
import Intro from './Intro';
import Button, { ButtonStyles } from './Button';
import SearchFormAddressBar from './SearchFormAddressBar';
import styles from './SearchForm.module.scss';
import {
	PAGE_LANDING,
	PAGE_SERP_LIST,
	PAGE_SERP_MAP,
	TC_EVENT_NAMES
} from '../utils/Constants';
import { useGoogleMapsApi } from '../api/gis';
import { useDeviceDetection } from '../utils/DeviceDetection';
import { useTCLog } from '../utils/TCEvent';
import { useDataSources } from '../api/dataSources';
import { SHOW_TITLE } from '../api/portletConfiguration';
import MapControlsButton from './MapControlsButtons';

export default function SearchForm({
	page,
	onPageChange,
	rankedSearch,
	isMunicipality,
	onToggleFilters,
	onSearchSubmit,
	onGeoLocationEnable,
	autocompleteResults,
	setAutocompleteResult,
	inputValue,
	setInputValue,
	openInfoWindow,
	dataSrc,
	searchParameters,
	showFiltersButton,
	setRankedSorting
}) {
	const google = useGoogleMapsApi();
	const [TCLog] = useTCLog();
	const isMobile = useDeviceDetection();

	const [showClearButton, setShowClearButton] = React.useState(false);

	const clearAddressStyle = {
		left: 'calc(100% - 40px)',
		top: '0',
		borderRightWidth: '0',
		borderLeftWidth: '0',
		opacity: '0.5',
		width: '40px',
		height: '40px',
		position: 'absolute',
		color: '#d2d2d2',
		display: showClearButton ? 'block' : 'none'
	};

	const userPositionStyle = {
		left: isMobile ? 'calc(100% - 80px)' : '80%',
		color: '#d2d2d2',
		width: '40px',
		height: '40px',
		position: 'absolute'
	};

	const searchSubmitStyle = {
		fontSize: '16px',
		width: isMobile ? '40px' : 'calc(20% - 40px)',
		height: '40px',
		position: 'absolute',
		right: '0'
	};

	function handleInputChange(event, nextInputValue) {
		setInputValue(nextInputValue);
	}

	function handleGeoLocation(event) {
		if (event) event.preventDefault();

		navigator.geolocation.getCurrentPosition((position) => {
			// TODO Reverse geo-location to obtain the description
			onGeoLocationEnable();
			TCLog(TC_EVENT_NAMES.USER_POSITION, true);

			const geocoder = new google.maps.Geocoder();

			const latlng = {
				lat: parseFloat(position.coords.latitude),
				lng: parseFloat(position.coords.longitude)
			};
			let description;

			geocoder.geocode({ location: latlng }, (results, status) => {
				if (status === 'OK') {
					const result = results[0];
					if (result) {
						description = result.formatted_address;
						setInputValue(description);
						onSearchSubmit({
							formattedAddress: description,
							lat: position.coords.latitude,
							lng: position.coords.longitude,
							place: result
						});
					}
				} else {
					setInputValue('');
					console.error('Geocoder failed due to: ' + status);
				}
			});

			onSearchSubmit({
				formattedAddress: description,
				lat: position.coords.latitude,
				lng: position.coords.longitude
			});
		}, showPositionError);
	}

	function showPositionError(error) {
		TCLog(TC_EVENT_NAMES.USER_POSITION, false);
		showError(error);
	}

	function showError(error) {
		switch (error.code) {
			case error.PERMISSION_DENIED:
				console.log('User denied the request for Geolocation.');
				break;
			case error.POSITION_UNAVAILABLE:
				console.log('Location information is unavailable.');
				break;
			case error.TIMEOUT:
				console.log('The request to get user location timed out.');
				break;
			case error.UNKNOWN_ERROR:
				console.log('An unknown error occurred.');
				break;
			default:
				console.log('An unknown error occurred.');
		}
	}

	function handleSubmit(event) {
		event.preventDefault();

		if (!inputValue) return;

		const geocoder = new google.maps.Geocoder();

		geocoder.geocode(
			{
				address: inputValue,
				componentRestrictions: {
					country: 'it'
				}
			},
			function (results, status) {
				if (status === google.maps.GeocoderStatus.OK) {
					const result = results[0];

					if (result) {
						const description = result.formatted_address;
						const point = result['geometry']['location'];

						onSearchSubmit({
							formattedAddress: description,
							lat: point.lat(),
							lng: point.lng(),
							place: result
						});
					}
				} else {
					setInputValue('');
					console.error('Geocoder failed due to: ' + status);
				}
			}
		);
	}

	React.useEffect(() => {
		if (google && !searchParameters) {
			handleGeoLocation();
		}
	}, [google]);

	return (
		<form className={styles.root} onSubmit={handleSubmit}>
			{page === PAGE_LANDING ? <Intro dataSrc={dataSrc}></Intro> : null}
			{page != PAGE_LANDING && SHOW_TITLE && dataSrc ? (
				<div className={styles.searchBarHeader}>
					<div>
						<h2 className={styles.searchBarTitle}>
							<Icon
								className={styles.icon}
								icon={dataSrc.icon}
								size="large"
								textOn="right"
							/>
							Cerca {dataSrc.label}
						</h2>
					</div>
					<div
						className={clsx(
							styles.mapControlsRow,
							page === PAGE_SERP_LIST ? styles.toBottom : null
						)}
					>
						<MapControlsButton
							page={page}
							onPageChange={onPageChange}
							rankedSearch={rankedSearch}
							isMunicipality={isMunicipality}
							onToggleFilters={onToggleFilters}
							showFiltersButton={showFiltersButton}
							setRankedSorting={setRankedSorting}
						></MapControlsButton>
					</div>
				</div>
			) : null}
			<div className={styles.addressInputRow}>
				<div
					className={styles.addressInputContainer}
					onMouseOver={() => setShowClearButton(true)}
					onMouseOut={() => setShowClearButton(false)}
				>
					<SearchFormAddressBar
						className={styles.input}
						type="text"
						placeholder={'Inserisci indirizzo, CAP o città'}
						inputValue={inputValue}
						onInputChange={handleInputChange}
						onSubmit={onSearchSubmit}
						autocompleteResults={autocompleteResults}
						setAutocompleteResult={setAutocompleteResult}
						openInfoWindow={openInfoWindow}
					/>

					<Button
						variant={ButtonStyles.SECONDARY}
						icon={<Icon icon="icon/close_grey.png" />}
						style={clearAddressStyle}
						type="button"
						onClick={() => setInputValue('')}
					/>
				</div>

				<Button
					variant={ButtonStyles.SECONDARY}
					icon={<Icon icon="icon/geolocator_grey.png" />}
					style={userPositionStyle}
					onClick={handleGeoLocation}
					type="button"
				/>

				<Button
					variant={ButtonStyles.PRIMARY}
					style={searchSubmitStyle}
					icon={
						isMobile ? <Icon icon="icon/cerca_white.png" /> : null
					}
					text={isMobile ? null : 'Cerca'}
				/>
			</div>

			{page != PAGE_LANDING && !SHOW_TITLE ? (
				<div
					className={clsx(
						styles.mapControlsRow,
						page === PAGE_SERP_LIST ? styles.toBottom : null
					)}
				>
					<MapControlsButton
						page={page}
						onPageChange={onPageChange}
						rankedSearch={rankedSearch}
						isMunicipality={isMunicipality}
						onToggleFilters={onToggleFilters}
						showFiltersButton={showFiltersButton}
						setRankedSorting={setRankedSorting}
					></MapControlsButton>
				</div>
			) : null}
		</form>
	);
}
